import { FC, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AccountContext } from "auth/accountProvider";
import { NO_ACCESS_PATH } from "auth/protectedRoute";

/**
 * Re-checks the signed-in user's roles on every route change.
 *
 * Roles used to be captured once at login and rehydrated from localStorage
 * forever, so demoting someone in Zitadel (editor -> viewer) left them looking
 * at editor-only controls — the Data menu item, the delete buttons — until
 * they happened to log out. The backend refused those actions, so it was never
 * a security hole, but the UI offered controls that only ever produced a 403,
 * which reads as a broken app.
 *
 * Navigation is the trigger, on purpose: no polling interval and no
 * window-focus listener. A user who is sitting still is not being surprised by
 * anything, and the first page they move to is exactly where a stale grant
 * would have shown them a control that does not work.
 *
 * The backend caches the role lookup for five minutes, so this is a cheap call
 * even on a click-happy session.
 */
export const useRoleRefresh = (): void => {
  const { pathname } = useLocation();
  const { refreshRoles, isAuthenticated } = useContext(AccountContext);

  // refreshRoles is rebuilt whenever the session tokens change, and it CHANGES
  // ITSELF by writing roles. Depending on it directly would re-run the effect
  // on every one of those rebuilds — a request storm, and a self-feeding one.
  // The effect below therefore depends only on the pathname, and reaches the
  // current callback through this ref.
  const refreshRolesRef = useRef(refreshRoles);
  useEffect(() => {
    refreshRolesRef.current = refreshRoles;
  }, [refreshRoles]);

  // The route we have already re-checked. Renders that do not change the route
  // (state updates, context changes, StrictMode double-invokes) must not fire
  // a second request for the same page.
  const checkedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Login, password reset, and the pre-session boot render. Nothing to ask
      // about, and no headers to ask with. Forgetting the last checked route
      // here means the next sign-in re-checks even if it lands on the same URL.
      checkedPathRef.current = null;
      return;
    }

    // /no-access is where ProtectedRoute sends role denials, and it is
    // deliberately outside every role gate so that redirect terminates.
    // Refreshing here would re-render the tree behind a <Navigate> that fires
    // from an effect with no dependency array, i.e. re-navigates on every
    // render — the loop that previously hung the browser. A user sitting on
    // /no-access picks up new roles by navigating anywhere else.
    if (pathname === NO_ACCESS_PATH) return;

    if (checkedPathRef.current === pathname) return;
    checkedPathRef.current = pathname;

    // Fire and forget: refreshRoles never rejects.
    refreshRolesRef.current();
  }, [pathname, isAuthenticated]);
};

/**
 * Mount-once driver for useRoleRefresh. Renders nothing; it exists so the hook
 * can live inside the Router while AccountProvider stays above it.
 */
export const RoleRefresh: FC = () => {
  useRoleRefresh();
  return null;
};
